import React, { FC, useEffect, useRef, useState } from "react";
import Loader from "@shared/components/Loader";
import { toastService } from "@services/ToastService";
import useRoleColor from "./utils/useRoleColor";
import { useUserService } from "@utils/useUsers";
import { useProject } from "@utils/useProject";
import { useReport } from "@utils/useReports";
import { ShareMenuProps, Member, UserRole, Email } from "./utils/interfaces";
import { useUser } from "@services/UserContext";

const ShareMenu: FC<ShareMenuProps> = ({
  isOpen,
  projectId,
  reportId,
  ownerFirstName,
  ownerLastName,
  reducedVersion,
  onMemberUpdate,
  publicAccess,
  reportPublicId,
  isAdmin = false,
}) => {
  const [users, setUsers] = useState<Member[]>([]);
  const [email, setEmail] = useState("");
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [emails, setEmails] = useState<Email[]>([]);
  const [userSelected, setUserSelected] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPublic, setIsPublic] = useState(false);

  const { user: userLoggedIn } = useUser();

  const isDisabled = false;

  const { getProjectMembers, grantProjectRole, revokeProjectRole } =
    useProject();
  const {
    fetchReportMembers,
    grantReportRole,
    revokeReportRole,
    toggleReportPublicAccess,
  } = useReport();
  const { searchCompanyMembers, getRoles } = useUserService();

  const fetchMembers = async () => {
    try {
      let response;
      if (reportId) {
        response = await fetchReportMembers(projectId, reportId);
      } else {
        response = await getProjectMembers(projectId);
      }
      if (response) {
        const data = response as Member[];
        setUsers(data.sort((a, b) => a.id - b.id));
        if (onMemberUpdate) {
          onMemberUpdate(data);
        }
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  useEffect(() => {
    setActiveDropdown(null);
    if (isOpen) {
      setIsVisible(true);
      fetchMembers();
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (activeDropdown !== null) {
      const dropdown = dropdownRefs.current[activeDropdown];
      if (dropdown) {
        const rect = dropdown.getBoundingClientRect();
        const isOverflowingRight = rect.right > window.innerWidth;
        const isOverflowingBottom = rect.bottom > window.innerHeight;

        if (isOverflowingRight) {
          dropdown.style.left = "auto";
          dropdown.style.right = "0";
        }

        if (isOverflowingBottom) {
          dropdown.style.top = "auto";
          dropdown.style.bottom = "100%";
        }
      }
    }
  }, [activeDropdown]);

  const grantRole = async (email: string, roleId: number) => {
    try {
      let response;
      if (reportId) {
        response = await grantReportRole(projectId, reportId, email, roleId);
      } else {
        response = await grantProjectRole(projectId, email, roleId);
      }
      return response;
    } catch (error) {
      console.error("Error granting role:", error);
      setIsLoading(false);
      return false;
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) return;
    setIsLoading(true);

    if (users.findIndex((user) => user.user_email === email) !== -1) {
      setIsLoading(false);
      toastService.warn(
        "User already has access to this project, You can change their role from the list below."
      );
      return;
    }

    const success = await grantRole(email, roles[2].id);
    if (success) {
      setIsLoading(false);
      await fetchMembers();
      setEmail("");
    } else {
      setIsLoading(false);
      toastService.error(
        "Failed to invite user. Please make sure the user exists in the company."
      );
    }
  };

  const updateRole = async (
    userIndex: number,
    roleId: number,
    newPermission: string
  ) => {
    const success = await grantRole(users[userIndex].user_email, roleId);
    if (success) {
      fetchMembers();
      setUsers((prev) =>
        prev.map((user, index) =>
          index === userIndex ? { ...user, role_name: newPermission } : user
        )
      );
    }
    setActiveDropdown(null);
  };

  const removeUser = async (userIndex: number) => {
    try {
      const user = users[userIndex];

      if (reportId) {
        await revokeReportRole(projectId, reportId, user.id);
      } else {
        await revokeProjectRole(projectId, user.id);
      }

      setUsers((prev) => prev.filter((_, index) => index !== userIndex));
      setActiveDropdown(null);
      await fetchMembers();
    } catch (error) {
      console.error("Error removing user:", error);
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSelect = (selectedEmail: string) => {
    setEmail(selectedEmail);
    setShowDropdown(false);
    setUserSelected(true); // Prevent refetching
    setIsValidEmail(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.trim()); // trims leading & trailing spaces
    setUserSelected(false); // Allow fetching again when typing
  };

  const handleCopyReportLink = async () => {
    const baseUrl = window.location.origin;
    const textToCopy = `${baseUrl}/project/0/report/${reportPublicId}/public`;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const tempTextarea = document.createElement("textarea");
        tempTextarea.value = textToCopy;
        tempTextarea.style.position = "absolute";
        tempTextarea.style.left = "-9999px";
        document.body.appendChild(tempTextarea);

        tempTextarea.select();
        document.execCommand("copy");
        document.body.removeChild(tempTextarea);
      }

      toastService.success("Link copied to clipboard");
    } catch (error) {
      console.error("Failed to copy text to clipboard: ", error);
      toastService.error("Failed to copy link. Try again!");
    }
  };

  useEffect(() => {
    const shouldFetch = email.length > 0 && !userSelected;

    if (shouldFetch) {
      const fetchUsers = async () => {
        try {
          const usersFound = await searchCompanyMembers(email);

          if (usersFound) {
            setEmails(
              usersFound.filter(
                (user: Email) => !users.some((u) => u.user_email === user.email)
              )
            );
            setShowDropdown(true);
          }
        } catch (error) {
          console.error("Error fetching users:", error);
        }
      };

      fetchUsers();
    } else {
      setShowDropdown(false);
    }

    setIsValidEmail(
      validateEmail(email) || emails.some((e) => e.email === email)
    );
  }, [email, userSelected]);

  const fetchRoles = async () => {
    try {
      const type = reportId ? "reports" : "projects";
      const sortedRoles = await getRoles(type);
      setRoles(sortedRoles);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    fetchRoles();
    setIsPublic(publicAccess || false);
  }, []);

  const togglePublicAccess = async () => {
    let success = false;

    if (reportId) {
      try {
        success = await toggleReportPublicAccess(
          projectId,
          reportId,
          !isPublic
        );

        if (success) {
          setIsPublic(!isPublic);
        } else {
          console.error("Failed to update public access");
        }
      } catch (error) {
        console.error("Error updating public access:", error);
      }
    }
  };
  if (!isOpen) return null;

  return (
    <>
      {(users.length > 0 || !reducedVersion) && (
        <div
          ref={modalRef}
          className={`absolute right-0 mt-2 bg-white border rounded-[0.2rem] shadow-lg w-[36rem] z-50 transform transition-all duration-300 ${
            isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"
          }`}
          style={{ top: "100%" }}
        >
          <div className="p-4 pb-2">
            {!reducedVersion && (
              <>
                <div className="mb-5">
                  <h2 className="text-base 2xl:text-lg font-semibold ">
                    Share {reportId ? "Report" : "Project"}
                  </h2>
                  <p className="text-gray-400 text-sm 2xl:text-base">
                    Enter Name or email
                  </p>
                </div>

                <div className="relative w-full mb-5">
                  <input
                    type="text"
                    placeholder="Share with.."
                    className="w-full border text-sm 2xl:text-base rounded-[0.2rem] px-3 py-4 pr-20 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    value={email}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault(); // stops default form submit
                        e.stopPropagation(); // stops event reaching Mapbox
                        if (isValidEmail && !isLoading) {
                          handleInvite();
                        }
                      }
                    }}
                  />
                  {showDropdown && emails.length > 0 && (
                    <ul className="absolute w-full mt-1 bg-white text-sm 2xl:text-base border rounded-sm shadow-lg z-[99]">
                      {emails.map((e, index) => (
                        <li
                          key={index}
                          className="p-2 cursor-pointer hover:bg-gray-200 leading-none"
                          onClick={() => handleSelect(e.email)}
                        >
                          <span>
                            {e.first_name} {e.last_name}
                          </span>
                          <br />
                          <span className="text-[0.65rem] text-gray-400">
                            {e.email}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    type="button"
                    className={`max-w-24 absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-[0.6rem] rounded-md flex space-x-2 text-xs 2xl:text-sm
                    ${
                      isValidEmail && !isLoading
                        ? "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                        : "bg-gray-400 text-gray-200 cursor-not-allowed"
                    }`}
                    onClick={handleInvite}
                    disabled={!isValidEmail || isLoading}
                  >
                    {isLoading ? <Loader color="#fff" /> : <span>Invite</span>}
                  </button>
                </div>
              </>
            )}

            {users.length > 0 && (
              <>
                <h3
                  className={`${
                    reducedVersion && "font-semibold"
                  } text-gray-800 mb-5 text-sm 2xl:text-base`}
                >
                  People with access
                </h3>
                <div className="">
                  {users.map((user, index) => {
                    const bgColor = useRoleColor(user.ordering);
                    return (
                      <>
                        <div
                          key={index}
                          className="flex justify-between items-center mb-5 relative"
                        >
                          {" "}
                          <div className="flex items-center space-x-3 text-sm">
                            <span
                              className={`flex items-center justify-center w-8 h-8 rounded-full text-white ${bgColor}`}
                            >
                              {user.first_name.charAt(0)}
                            </span>
                            <div>
                              <p className="text-gray-800 font-medium leading-[1.1]">
                                {user.first_name} {user.last_name} <br />
                                <span className="text-gray-500 text-[0.6rem]">
                                  {user.user_email}
                                </span>
                              </p>
                            </div>
                          </div>
                          <div className="relative">
                            <button
                              type="button"
                              className="text-blue-600 font-semibold text-xs 2xl:text-sm flex items-center space-x-2 disabled:text-gray-400"
                              onClick={() =>
                                setActiveDropdown((prev) =>
                                  prev === index ? null : index
                                )
                              }
                              disabled={
                                isDisabled ||
                                !isAdmin ||
                                userLoggedIn?.email === user.user_email
                              }
                            >
                              <span>{user.role_name}</span>{" "}
                              {isAdmin &&
                                userLoggedIn?.email !== user.user_email && (
                                  <span>
                                    <img
                                      src="/arrow_dropdown_blue.svg"
                                      className="w-3 h-3"
                                      alt=""
                                    />
                                  </span>
                                )}
                            </button>
                            {activeDropdown === index && (
                              <div
                                ref={(el) => {
                                  dropdownRefs.current[index] = el;
                                }}
                                className="absolute bg-white border rounded-[0.2rem] shadow-2xl mt-3 w-[30rem] z-10 p-3"
                              >
                                {roles.length > 0 &&
                                  roles.map((role) => (
                                    <div
                                      key={role.id} // Added key prop for React list rendering
                                      className="p-2 hover:bg-gray-100 cursor-pointer"
                                      onClick={() =>
                                        updateRole(index, role.id, role.name)
                                      }
                                    >
                                      <div className="flex items-center space-x-2 text-sm 2xl:text-base">
                                        <div>
                                          <p>{role.name}</p>
                                          <p className="text-xs text-gray-500">
                                            {role.description}
                                          </p>
                                        </div>
                                        <div>
                                          {user.role_name === role.name && (
                                            <img
                                              src="/check_small.svg"
                                              className="w-3 h-3"
                                              alt="Checked"
                                            />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}

                                <div
                                  className="p-2 hover:bg-red-100 text-red-600 cursor-pointer text-sm 2xl:text-base"
                                  onClick={() => removeUser(index)}
                                >
                                  <p className="">Remove</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    );
                  })}
                </div>
              </>
            )}

            {reportId && (
              <div className="flex flex-col items-start space-y-2 py-3 border-t-2">
                <div className="flex items-center justify-between w-full text-sm 2xl:text-base">
                  <span className="text-gray-800">
                    Public access (Read Only)
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={isPublic}
                      onChange={() => togglePublicAccess()}
                    />
                    <div className="w-9 h-5 bg-gray-300 rounded-full peer-checked:bg-blue-600 transition relative">
                      <div
                        className={`w-4 h-4 bg-white rounded-full absolute top-0.5 left-1 transition ${
                          isPublic ? "translate-x-4" : ""
                        }`}
                      ></div>
                    </div>
                  </label>
                </div>

                {isPublic && (
                  <button
                    type="button"
                    className="flex items-center space-x-1 text-black self-center pt-1"
                    onClick={handleCopyReportLink}
                  >
                    <img src="/link.svg" className="w-5" alt="" />
                    <span>Copy link</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ShareMenu;
